import Fuse from "fuse.js";
import { t } from "i18next";
import Feature from "ol/Feature";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OpenIcon } from "../../assets/icons/OpenIcon";
import LOCAL_STORAGE from "../../constants/LocalStorage";
import { customerInfoUrl } from "../../constants/Routes";
import { ICustomer } from "../../utils/data-types";
import { saveToStore } from "../../utils/local-storage";
import Search from "../search/Search";
import { Text } from "../text/Text";
import "./CustomersTable.scss";

export const CustomersTable = ({
  data,
  features,
  onSelectedIdChange,
}: {
  data: ICustomer[] | undefined;
  features: Feature[];
  onSelectedIdChange: (id: number | undefined) => void;
}) => {
  const [filteredData, setFilteredData] = useState<ICustomer[]>([]);
  const prevFilteredDataRef = useRef<ICustomer[]>([]);
  const [selectedId, setSelectedId] = useState<number>();

  const handleRowClick = useCallback(
    (id: number) => {
      if (id === selectedId) {
        setSelectedId(undefined);
        onSelectedIdChange(undefined);
      } else {
        setSelectedId(id);
        onSelectedIdChange(id);
      }
    },
    [onSelectedIdChange, selectedId]
  );

  const navigate = useNavigate();

  const handleUserClick = useCallback(
    (id: number) => {
      saveToStore(LOCAL_STORAGE.CUSTOMER, id);
      navigate(customerInfoUrl);
    },
    [navigate]
  );

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  useEffect(() => {
    prevFilteredDataRef.current = filteredData;
  }, [filteredData]);

  const hasGeom = useCallback(
    (id: number) => {
      return features.some((feature) => feature.getId() === id);
    },
    [features]
  );

  useEffect(() => {
    if (filteredData && filteredData.length > 0) {
      const storedSearchTerm = localStorage.getItem(
        LOCAL_STORAGE.CUSTOMER_SEARCH_TERM
      );

      if (storedSearchTerm) {
        const fuse = new Fuse(filteredData, {
          keys: ["navn", "id", "adresse"],
          threshold: 0.3,
        });

        const results = fuse.search(storedSearchTerm);
        const newFilteredData = results.map((result) => result.item);

        if (
          JSON.stringify(newFilteredData) !==
          JSON.stringify(prevFilteredDataRef.current)
        ) {
          setFilteredData(newFilteredData);
        }
      } else {
        setFilteredData(data || []);
      }
    }
  }, [filteredData, data, prevFilteredDataRef]);

  const handleSearch = useCallback((searchResults: ICustomer[]) => {
    setFilteredData(searchResults);
  }, []);

  return (
    <div className="customers-table" id="table">
      <div className="customers-table__header">
        <Text
          text={t("pages.customers.title")}
          size="small"
          color="dark"
          fontWeight={600}
        />
        <div className="total-customers">
          <Text
            className="total-customers__text"
            text={
              (filteredData?.length ?? 0).toString() +
              " " +
              t("pages.customers.total")
            }
            size="xxxs"
            fontWeight={600}
          />
        </div>
        <Search
          placeholder={t("pages.customers.searchCustomersPlaceholder")}
          data={data}
          onSearch={handleSearch}
          type="customer"
        />
      </div>

      <div className="customers-table__content">
        <div className="table-header">
          <div className="table-cell">
            <Text text={t("pages.customers.name")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customers.customerId")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customers.address")} size="xxxs" />
          </div>
          <div className="table-cell"></div> {/* Empty cell for alignment */}
        </div>
        <div className="customers-table__scrollable-content">
          {filteredData &&
            filteredData.map((row, rowIndex) => (
              <div
                className={`table-row ${
                  hasGeom(filteredData[rowIndex].id) ? "has-geom" : ""
                } ${
                  selectedId === filteredData[rowIndex].id &&
                  hasGeom(selectedId)
                    ? "selected"
                    : ""
                }`}
                key={rowIndex}
                onClick={() => handleRowClick(filteredData[rowIndex].id)}
              >
                <div className="table-cell">
                  <Text
                    text={filteredData[rowIndex].navn}
                    size="xxs"
                    fontWeight={600}
                  />
                </div>
                <div className="table-cell">
                  <Text
                    text={filteredData[rowIndex].id.toString()}
                    size="xxs"
                  />
                </div>
                <div className="table-cell">
                  <Text text={filteredData[rowIndex].adresse} size="xxs" />
                </div>
                <div className="table-cell">
                  <div className="btn-container">
                    <OpenIcon
                      onClick={() => handleUserClick(filteredData[rowIndex].id)}
                    />
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
